import React, { useState, useEffect, useCallback } from "react";
import Navigation from "../../../App/Navigation";
import { ROUTES } from "../../../../consts";
import { useTrackContext } from "../../../../hooks/useTrackContext";
import TrackTile from "../../../TrackSelection/TrackSelectionTile";
import { SampleVariantsBlock } from "../../../common/ProductSectionAndDate/SampleVariantBlock";
import { ShoppingCartIcon } from "@heroicons/react/outline";
import ProgressBar from "../../../ProgressiveBar";
// import ModalWithAccordion from "../../../ModalAccordion";
import { getAdminIdFromStoreFrontId } from "../../../../helpers/shopify";
import { ImSpinner6 } from "react-icons/im";
import { formatDateWithSuffix } from "../../../../helpers/utils";
import MiniCart from "../../../Cart/MiniCart";
import UpsellModal from "../../../upsellModal";
import { handleAddToCartClick } from "../../../../utils/cartOperations";
import { IGVariant, TSelectedVariants } from "../../../../types";

interface UniqueVariant {
  variantSelectedTime: string;
  uniqueVariants: IGVariant[];
}
// vercel preview has failed - push again
const ReviewExperience: React.FC = () => {
  const {
    juniorTrackSelection,
    trackSelectionOption,
    productRenderedLocation,
    headlessCartInfo,
    dispatch,
    // termsAndConditionsContent,
    metaObjectTrackSelectionConfig,
    headlessCart,
  } = useTrackContext();

  const [totalPrice, setTotalPrice] = useState<number>(0);

  const {
    variantsAndDate: { selectedVariants },
    racesSeats: { numberOfSeats },
    isTermsAndConditionExcepted,
  } = juniorTrackSelection;

  const [uniqueVariants, setUniqueVariants] = useState<UniqueVariant[]>([]);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [isProductAdding, setIsProductAdding] = useState<boolean>(false);
  const [isBookingNow, setIsBookingNow] = useState<boolean>(false);
  const [isDuplicateModal, setIsDuplicateModal] = useState<boolean>(false);
  const [duplicateVariants, setDuplicateVariants] = useState<TSelectedVariants[]>([]);
  const [nextRoute, setNextRoute] = useState<string>("#");

  const handleAddToCart = useCallback(async () => {
    try {
      setIsProductAdding(true);
      const result = await handleAddToCartClick(selectedVariants, numberOfSeats, productRenderedLocation, headlessCart, headlessCartInfo, dispatch);

      if (!result.success) {
        if (result.duplicates) {
          setDuplicateVariants(result.duplicates);
          setIsDuplicateModal(true);
        } else if (result.error) {
          alert(result.error);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsProductAdding(false);
    }
  }, [dispatch, headlessCartInfo, numberOfSeats, productRenderedLocation, selectedVariants, headlessCart]);

  const handleBookNow = useCallback(async () => {
    try {
      setIsBookingNow(true);
      await handleAddToCart();
      if (productRenderedLocation === "external") {
        if (headlessCartInfo.checkoutUrl) {
          window.open(headlessCartInfo.checkoutUrl, "_blank");
        }
      } else {
        window.location.href = "/checkout";
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsBookingNow(false);
    }
  }, [headlessCartInfo, handleAddToCart, productRenderedLocation]);

  useEffect(() => {
    let newTotalPrice = 0;

    const uniqueVariantsByTime: { [time: string]: UniqueVariant } = {};

    selectedVariants.forEach((variant) => {
      const variantSelectedTime = variant.variantSelectedTime.toString();
      newTotalPrice += parseFloat(variant.variantsSelectedObject.priceV2.amount);
      if (!uniqueVariantsByTime[variantSelectedTime]) {
        uniqueVariantsByTime[variantSelectedTime] = {
          variantSelectedTime,
          uniqueVariants: [],
        };
      }

      (uniqueVariantsByTime[variantSelectedTime].uniqueVariants as IGVariant[]).push(variant.variantsSelectedObject as unknown as IGVariant);
    });

    setTotalPrice(newTotalPrice);

    const uniqueVariantsArray: UniqueVariant[] = Object.values(uniqueVariantsByTime);

    uniqueVariantsArray.sort((a, b) => {
      const dateA = new Date(a.variantSelectedTime);
      const dateB = new Date(b.variantSelectedTime);

      const monthDiff = dateA.getMonth() - dateB.getMonth();
      if (monthDiff !== 0) {
        return monthDiff;
      }

      return dateA.getDate() - dateB.getDate();
    });

    setUniqueVariants(uniqueVariantsArray);
  }, [productRenderedLocation, selectedVariants]);

  useEffect(() => {
    if (!isTermsAndConditionExcepted) {
      const element = document.getElementById("terms-and-condition-wrapper") as HTMLElement;
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
      setNextRoute("#");
    } else {
      setNextRoute("/" + ROUTES.JUNIOR_TRACK.ReviewCart);
    }
    const buttonElement = document.getElementById("terms-and-conditions");
    if (nextRoute === "#") {
      if (buttonElement) {
        buttonElement.classList.add("tw-animate-bounce");
      }
    } else {
      if (buttonElement) {
        buttonElement.classList.remove("tw-animate-bounce");
      }
    }
  }, [isTermsAndConditionExcepted, nextRoute]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const trackTitle = metaObjectTrackSelectionConfig?.juniorTrack?.title ? metaObjectTrackSelectionConfig.juniorTrack.title : trackSelectionOption.title;
  const videoBannerUrl = metaObjectTrackSelectionConfig?.juniorTrack?.review_video_url;

  return (
    <>
      {isDuplicateModal && duplicateVariants.length > 0 && (
        <UpsellModal
          modalTitle="You've already added this to your cart, are you sure you want to add it again?"
          toggleModalDisplay={() => setIsDuplicateModal(false)}
          isModalOpen={isDuplicateModal}
          trackOptions={trackSelectionOption}
          upsellVariants={duplicateVariants}
          numberOfSeats={juniorTrackSelection.racesSeats.numberOfSeats}
          raceType={"STANDARD"}
          productRenderedLocation={productRenderedLocation}
          headlessCartInfo={headlessCartInfo}
          isDuplicateModal={duplicateVariants.length > 0}
        />
      )}

      <ProgressBar maxValue={100} currentValue={windowWidth <= 768 ? 90 : 85} />
      {productRenderedLocation === "external" && <MiniCart />}
      <div className="container">
        <div className="tw-block lg:tw-hidden">
          <span className="tw-block tw-text-[18px] lg:tw-text-[24px] tw-text-[#F609FF] tw-font-[700] tw-mt-[30px] tw-text-center">Junior Track</span>
          <div className="tw-text-white tw-text-center tw-text-[24px] tw-font-extrabold">Review your Experience</div>
        </div>
        <div className="tw-hidden lg:tw-block">
          <Navigation
            previous={{ active: true, to: "/" + ROUTES.JUNIOR_TRACK.DATE_TIME }}
            next={{ active: nextRoute !== "#", to: nextRoute }}
            title={"Review your Experience"}
            subtitle={"Junior Track"}
          />
        </div>

        <div className="tw-flex tw-justify-center tw-mt-[56px]">
          <div className="tw-block lg:tw-flex lg:tw-items-center lg:tw-gap-x-16">
            <TrackTile
              onDoubleClick={() => console.log("Double Click has fired")}
              isSelected={false}
              displayBorders={true}
              title={trackTitle}
              image={trackSelectionOption.trackBanner}
              videoBannerUrl={videoBannerUrl}
            />
            <div className="tw-text-white">
              <span className="tw-text-[16px] tw-font-[600] tw-flex tw-justify-between tw-mt-[10px]">
                <span>Seat(s):</span> <span id="mcp-variant-quantity">{juniorTrackSelection.racesSeats.numberOfSeats}</span>
              </span>
              <span className="tw-text-[16px] tw-font-[600] tw-flex tw-justify-between tw-mt-[10px]">
                <span>Race(s):</span>
                <span>{juniorTrackSelection.racesSeats.numberOfRaces}</span>
              </span>
              {uniqueVariants.length &&
                uniqueVariants.map((variant) => {
                  return (
                    <div
                      key={variant.variantSelectedTime}
                      id="mcp-product-id-selector"
                      data-product-id={getAdminIdFromStoreFrontId(variant.uniqueVariants[0].product.id as string)}
                    >
                      <span className="tw-text-[18px] tw-font-[700] tw-block tw-my-[10px] tw-text-center lg:tw-text-left">
                        {formatDateWithSuffix(new Date(variant.variantSelectedTime))}
                      </span>
                      <div className="tw-grid tw-grid-cols-3 tw-gap-[20px] tw-mt-[10px]">
                        {variant.uniqueVariants &&
                          variant.uniqueVariants.map((xv) => {
                            return <SampleVariantsBlock variantId={xv.id} key={xv.id} title={xv.title} price={xv.priceV2.amount} />;
                          })}
                      </div>
                    </div>
                  );
                })}

              <span className="tw-text-[18px] tw-font-[600] tw-block tw-mt-[10px] tw-text-center lg:tw-text-left">Total</span>
              <span className="tw-text-[30px] tw-font-[700] tw-leading-[1] tw-mt-[10px] tw-block tw-text-center lg:tw-text-left">
                $<span>{Number(totalPrice * numberOfSeats).toFixed(2)}</span>
              </span>
              <span className="tw-text-[12px] md:tw-text-[14px] tw-font-[500] tw-block tw-my-[20px] tw-text-center lg:tw-text-left tw-flex tw-items-center tw-justify-center">
                Get <span className="tw-font-[700] tw-px-1">$19 off</span> with Hyper Karting{" "}
                <a href="https://hyperkarting.com.au/products/membership">
                  <img
                    src="https://cdn.shopify.com/s/files/1/0496/8733/0976/files/HK-Membership_Icon_a819308e-9913-473d-b1ac-b5aa3d061947.png?v=1695350485"
                    alt="membership logo"
                    style={{
                      maxWidth: "150px",
                      width: "100%",
                    }}
                  />
                </a>
              </span>
              <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-[20px]">
                {isTermsAndConditionExcepted ? (
                  <>
                    {productRenderedLocation === "external" ? (
                      <>
                        <span
                          onClick={() => handleAddToCart()}
                          className={`tw-flex tw-items-center tw-justify-center tw-text-center tw-text-white lg:tw-max-w-[230px] lg:tw-w-full tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] bg-gradient-2 tw-rounded-full tw-cursor-pointer`}
                          id="add-to-cart-button"
                        >
                          <>
                            {isProductAdding ? (
                              <>
                                <ImSpinner6 className="tw-w-[26px] tw-h-[26px] tw-animate-spin tw-transition tw-duration-300 tw-ease-in-out" />
                                <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Adding...</span>
                              </>
                            ) : (
                              <>
                                <ShoppingCartIcon className="tw-w-[26px] tw-h-[26px]" />
                                <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Add to Cart</span>
                              </>
                            )}
                          </>
                        </span>
                        <span
                          onClick={() => handleBookNow()}
                          className="tw-flex tw-items-center tw-justify-center tw-text-center tw-font-extrabold tw-text-[medium] tw-text-[#9D62FE] tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] lg:tw-max-w-[230px] lg:tw-w-full tw-border-[#9D62FE] tw-border tw-rounded-full tw-cursor-pointer"
                        >
                          {isBookingNow ? "Booking..." : "Book Now"}
                        </span>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleAddToCart()}
                          className={`tw-flex tw-items-center tw-justify-center tw-text-center tw-text-white lg:tw-max-w-[230px] lg:tw-w-full tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] bg-gradient-2 tw-rounded-full tw-cursor-pointer`}
                          id="add-to-cart-button"
                        >
                          <>
                            {isProductAdding ? (
                              <>
                                <ImSpinner6 className="tw-w-[26px] tw-h-[26px] tw-animate-spin tw-transition tw-duration-300 tw-ease-in-out" />
                                <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Adding...</span>
                              </>
                            ) : (
                              <>
                                <ShoppingCartIcon className="tw-w-[26px] tw-h-[26px]" />
                                <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Add to Cart</span>
                              </>
                            )}
                          </>
                        </button>
                        <button
                          onClick={() => handleBookNow()}
                          className="tw-flex tw-items-center tw-justify-center tw-text-center tw-font-extrabold tw-text-[medium] tw-text-[#9D62FE] tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] lg:tw-max-w-[230px] lg:tw-w-full tw-border-[#9D62FE] tw-border tw-rounded-full tw-cursor-pointer"
                        >
                          {isBookingNow ? "Booking..." : "Book Now"}
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className={`tw-bg-[#2F1160] tw-w-fit tw-mx-auto tw-py-[8px] tw-px-[16px] tw-rounded-[8px] tw-mb-[15px] tw-mt-[30px]`}>
                      <span className={`tw-text-[red] tw-font-[700] tw-text-center tw-flex`}>Please accept Terms & Conditions to continue</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="tw-w-full lg:tw-h-[126px] tw-p-[30px] tw-mt-[40px] lg:tw-mt-0">
        <div className="tw-flex tw-justify-center tw-items-center">
          <div className="tw-block lg:tw-hidden">
            <Navigation
              previous={{ active: true, to: "/" + ROUTES.MAIN_TRACK.DATE_TIME }}
              next={{ active: true, to: "/" + ROUTES.MAIN_TRACK.ReviewCart }}
              title={"Review your experience"}
              subtitle={"Main Track"}
            />
          </div>
          <div
            className={`${productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"} tw-relative tw-flex tw-justify-end tw-items-center lg:tw-block`}
          >
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewExperience;
